import { useParams, useHistory } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { ApiAppointment, ApiFinance } from "../../../core/apis";
import { ApiSchemaAppointmentById } from "../../../models/api/appointment";
import { SpinLoader } from "../../../components/spinLoader.component";
import { UtilityDate } from "../../../utils/dateUtils";
import { AppRouteUi } from "../../../config/appRoute";
import { EnumInvoiceGenerationInvokeType } from "../../../libs/enums/EnumPatientInvoketype";
import { showSuccessToast } from "../../../components/toast.component";
import {
  FaUserNurse,
  FaUserInjured,
  FaClock,
  FaCalendarAlt,
  FaClipboardCheck,
  FaCog,
} from "react-icons/fa";
import { ApiInvoice } from "../../../core/apis/invoice";
import { ApiSchemaAppointmentInvoiceRecord } from "../../../models/api/invoice";

export const PageBackdatedInvoiceGeneration = () => {
  const { appointmentId } = useParams<{ appointmentId: string }>();
  const [isLoading, setIsLoading] = useState(true);
  const [isInvoiceRecordLoading, setIsInvoiceRecordLoading] = useState(true);
  const [data, setData] = useState<ApiSchemaAppointmentById>();
  const [invoiceRecordData, setInvoiceRecordData] = useState<
    ApiSchemaAppointmentInvoiceRecord[]
  >([]);
  const [isGeneratingInvoice, setIsGeneratingInvoice] = useState(false);
  const [processingButtonKey, setProcessingButtonKey] = useState<string | null>(
    null
  );
  const routeHistory = useHistory();

  useEffect(() => {
    setIsLoading(true);
    ApiAppointment.GetById(appointmentId).then((appointment) => {
      setData(appointment);
      setIsLoading(false);
    });
  }, [appointmentId]);

  useEffect(() => {
    const getInvoiceRecord = async () => {
      setIsInvoiceRecordLoading(true);
      const record = await ApiInvoice.GetInvoiceRecordByAppointmentId(
        appointmentId
      );
      if (record?.isSuccess && record?.data) {
        setInvoiceRecordData(record.data);
      }
      setIsInvoiceRecordLoading(false);
    };

    getInvoiceRecord();
  }, [appointmentId]);

  const getMonthsInRange = (startDateStr: string, endDateStr: string) => {
    const startDate = new Date(startDateStr);
    const endDate = new Date(endDateStr);
    const months = [];
    let currentDate = new Date(
      startDate.getFullYear(),
      startDate.getMonth(),
      1
    );

    while (currentDate <= endDate) {
      months.push({
        monthName: UtilityDate.monthNames[currentDate.getMonth()],
        year: currentDate.getFullYear(),
        monthIndex: currentDate.getMonth(),
      });
      currentDate.setMonth(currentDate.getMonth() + 1);
    }
    return months;
  };

  const handleGenerateInvoice = async (
    year: number,
    monthIndex: number,
    buttonKey: string
  ) => {
    var invoiceDateForApi = new Date(Date.UTC(year, monthIndex, 15));
    if (!data?.caregiverSKey || !data?.invoiceGenerationInvokeType) return;

    if (isNaN(invoiceDateForApi.getTime())) {
      console.error("Invalid invoice date");
      return;
    }
    const invoiceDateIso = invoiceDateForApi.toISOString();
    setIsGeneratingInvoice(true);
    setProcessingButtonKey(buttonKey);
    try {
      const response = await ApiFinance.InvoiceByCaregiverSkey(
        data.caregiverSKey,
        invoiceDateForApi,
        data.invoiceGenerationInvokeType
      );
      if (response?.isSuccess) {
        const monthName = UtilityDate.monthNames[monthIndex];
        showSuccessToast(
          `Invoice generated successfully for ${monthName} ${year}`
        );
      }
    } catch (error) {
      console.error("Error generating invoice:", error);
    } finally {
      setIsGeneratingInvoice(false);
      setProcessingButtonKey(null);
    }
  };

  const lastGeneratedDate = useMemo(() => {
    if (invoiceRecordData.length === 0) return null;

    return invoiceRecordData.reduce((latest, record) => {
      const recordDate = new Date(record.startDate);
      return !latest || recordDate > latest ? recordDate : latest;
    }, null as Date | null);
  }, [invoiceRecordData]);

  const isBeforeLastGenerated = (monthIndex: number, year: number) => {
    if (!lastGeneratedDate) return false;

    const targetDate = new Date(year, monthIndex);
    const lastGeneratedMonth = new Date(
      lastGeneratedDate.getFullYear(),
      lastGeneratedDate.getMonth()
    );

    return targetDate < lastGeneratedMonth;
  };

  const isNextAvailableMonth = (
    monthIndex: number,
    year: number,
    pastMonths: { monthIndex: number; year: number }[]
  ) => {
    // Sort months chronologically
    const sortedMonths = [...pastMonths].sort((a, b) => {
      if (a.year !== b.year) return a.year - b.year;
      return a.monthIndex - b.monthIndex;
    });

    // Find the first month that doesn't have an invoice generated
    const nextMonth = sortedMonths.find(
      (month) => !isBeforeLastGenerated(month.monthIndex, month.year)
    );

    if (!nextMonth) return false;

    return nextMonth.monthIndex === monthIndex && nextMonth.year === year;
  };

  return (
    <div>
      <div className="content-box bg-white">
        <div className="content-box-header">
          <div className="row align-items-center justify-content-between">
            <div className="col content-box-title">
              <div className="d-flex align-items-center">
                <div
                  onClick={() => {
                    routeHistory.length > 1
                      ? routeHistory.goBack()
                      : routeHistory.push(AppRouteUi.Root);
                  }}
                  className="pe-2 border-2 click text-green-200"
                >
                  <i className="fas fa-arrow-left text-green-200" />
                </div>
                <div className="d-flex flex-column border-start">
                  <p className="text-capitalize mb-0 ps-2 text-green-200">
                    Backdated Invoice Generation
                  </p>
                  {!isLoading && data && (
                    <p className="ms-2 pb-1 small-text">
                      ({UtilityDate.formatDate(data.appointmentStartDate)})
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="p-4">
          {isLoading ? (
            <SpinLoader />
          ) : (
            <div className="row">
              <div className="row mb-4">
                <div className="col-md-4">
                  <div className="p-3 border rounded shadow-sm h-100">
                    <div className="d-flex align-items-center mb-2">
                      <FaUserNurse className="text-green-200 me-2" size={20} />
                      <label className="fw-bold mb-0">Caregiver Name:</label>
                    </div>
                    <div className="ms-4">{data?.caregiverName}</div>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="p-3 border rounded shadow-sm h-100">
                    <div className="d-flex align-items-center mb-2">
                      <FaUserInjured
                        className="text-green-200 me-2"
                        size={20}
                      />
                      <label className="fw-bold mb-0">Patient Name:</label>
                    </div>
                    <div className="ms-4">{data?.patientName}</div>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="p-3 border rounded shadow-sm h-100">
                    <div className="d-flex align-items-center mb-2">
                      <FaClipboardCheck
                        className="text-green-200 me-2"
                        size={20}
                      />
                      <label className="fw-bold mb-0">
                        Appointment Status:
                      </label>
                    </div>
                    <div className="ms-4">
                      <span
                        className={`badge ${
                          data?.appointmentStatus === "Complete"
                            ? "bg-success"
                            : "bg-warning"
                        }`}
                      >
                        {data?.appointmentStatus}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-md-4">
                  <div className="p-3 border rounded shadow-sm h-100">
                    <div className="d-flex align-items-center mb-2">
                      <FaCalendarAlt
                        className="text-green-200 me-2"
                        size={20}
                      />
                      <label className="fw-bold mb-0">
                        Appointment Start Date:
                      </label>
                    </div>
                    <h5 className="ms-4">
                      {data &&
                        UtilityDate.formatHumanFriendlyDate(
                          data.appointmentStartDate
                        )}
                    </h5>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="p-3 border rounded shadow-sm h-100">
                    <div className="d-flex align-items-center mb-2">
                      <FaClock className="text-green-200 me-2" size={20} />
                      <label className="fw-bold mb-0">
                        Appointment End Date:
                      </label>
                    </div>
                    <h5 className="ms-4">
                      {data &&
                        UtilityDate.formatHumanFriendlyDate(
                          data.appointmentEndDate
                        )}
                    </h5>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="p-3 border rounded shadow-sm h-100">
                    <div className="d-flex align-items-center mb-2">
                      <FaCog className="text-green-200 me-2" size={24} />
                      <label
                        className="fw-bold mb-0"
                        style={{
                          fontSize: "1rem",
                          color: "#198754",
                          letterSpacing: "0.5px",
                        }}
                      >
                        Settings:
                      </label>
                    </div>
                    <div
                      className="ms-4"
                      style={{ fontSize: "1.1rem", fontWeight: 500 }}
                    >
                      <div className="mb-2 d-flex align-items-center">
                        <span style={{ marginRight: 8 }}>
                          Appointment Auto Clock in:
                        </span>
                        <span
                          className={`badge ${
                            data?.appointmentAutoClockInEnabled
                              ? "bg-success"
                              : "bg-secondary"
                          }`}
                          style={{ fontSize: "1rem", padding: "0.5em 1em" }}
                        >
                          {data?.appointmentAutoClockInEnabled
                            ? "Enabled"
                            : "Disabled"}
                        </span>
                      </div>
                      <div className="mb-2 d-flex align-items-center">
                        <span style={{ marginRight: 8 }}>
                          Caregiver Auto Clock in:
                        </span>
                        <span
                          className={`badge ${
                            data?.caregiverAutoClockInEnabled
                              ? "bg-success"
                              : "bg-secondary"
                          }`}
                          style={{ fontSize: "1rem", padding: "0.5em 1em" }}
                        >
                          {data?.caregiverAutoClockInEnabled
                            ? "Enabled"
                            : "Disabled"}
                        </span>
                      </div>
                      <div className="d-flex align-items-center">
                        <span style={{ marginRight: 8 }}>
                          Invoice Generation:
                        </span>
                        <span
                          className="badge bg-info"
                          style={{
                            fontSize: "1rem",
                            padding: "0.5em 1em",
                            color: "#0a3622",
                            background: "#b6effb",
                          }}
                        >
                          {data?.invoiceGenerationInvokeType}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* New Invoice Generation Section */}
              <div className="row mt-4">
                <div className="col-12">
                  <div className="p-3 border rounded shadow-sm h-100">
                    {data && !data.appointmentAutoClockInEnabled ? (
                      <div className="alert alert-warning mb-0">
                        You can only generate backdated invoice if the
                        appointment is set to auto clock in.
                      </div>
                    ) : (
                      <>
                        <div className="d-flex align-items-center mb-3">
                          <FaCalendarAlt
                            className="text-green-200 me-2"
                            size={20}
                          />
                          <label className="fw-bold mb-0 fs-5 text-green-200">
                            Generate Invoice For:
                          </label>
                        </div>
                        <div>
                          {!isInvoiceRecordLoading &&
                            data &&
                            data.invoiceGenerationInvokeType ===
                              EnumInvoiceGenerationInvokeType.Monthly &&
                            (() => {
                              const pastMonths = getMonthsInRange(
                                data.appointmentStartDate,
                                data.appointmentEndDate
                              ).filter((month) => {
                                const now = new Date();
                                return !(
                                  month.year > now.getFullYear() ||
                                  (month.year === now.getFullYear() &&
                                    month.monthIndex >= now.getMonth())
                                );
                              });

                              if (pastMonths.length === 0) {
                                return (
                                  <p className="text-muted">
                                    No past months available for invoice
                                    generation.
                                  </p>
                                );
                              }

                              return (
                                <>
                                  <div className="d-flex flex-wrap">
                                    {pastMonths.map((month) => {
                                      const buttonKey = `${month.year}-${month.monthIndex}`;
                                      const isGenerated = isBeforeLastGenerated(
                                        month.monthIndex,
                                        month.year
                                      );
                                      const isAvailable = isNextAvailableMonth(
                                        month.monthIndex,
                                        month.year,
                                        pastMonths
                                      );
                                      const buttonStyle = isGenerated
                                        ? "btn-success"
                                        : isAvailable
                                        ? "btn-outline-primary"
                                        : "btn-secondary";
                                      return (
                                        <button
                                          key={buttonKey}
                                          className={`btn ${buttonStyle} me-2 mb-2`}
                                          onClick={() =>
                                            handleGenerateInvoice(
                                              month.year,
                                              month.monthIndex,
                                              buttonKey
                                            )
                                          }
                                          disabled={
                                            isGeneratingInvoice ||
                                            isGenerated ||
                                            !isAvailable
                                          }
                                          title={
                                            isGenerated
                                              ? "Invoice already generated"
                                              : isAvailable
                                              ? "Generate invoice for this month"
                                              : "Please generate previous months' invoices first"
                                          }
                                        >
                                          {processingButtonKey === buttonKey ? (
                                            <span className="d-flex align-items-center">
                                              <span className="me-2">
                                                {month.monthName} {month.year}
                                              </span>
                                              <span
                                                className="spinner-border spinner-border-sm"
                                                role="status"
                                                aria-hidden="true"
                                              />
                                            </span>
                                          ) : (
                                            <span className="d-flex align-items-center">
                                              {month.monthName} {month.year}
                                              {isGenerated && (
                                                <i
                                                  className="fas fa-check ms-2"
                                                  style={{ color: "white" }}
                                                ></i>
                                              )}
                                            </span>
                                          )}
                                        </button>
                                      );
                                    })}
                                  </div>
                                  <small className="text-muted d-block mt-2">
                                    You cannot generate invoice for the current
                                    or future months.
                                    <br />• Green buttons indicate months where
                                    invoices have already been generated
                                    <br />• Blue outline button indicates the
                                    next month available for generation
                                    <br />• Gray buttons indicate months that
                                    will become available after generating
                                    previous months
                                  </small>
                                </>
                              );
                            })()}

                          {data &&
                            data.invoiceGenerationInvokeType !==
                              EnumInvoiceGenerationInvokeType.Monthly && (
                              <p className="text-muted">
                                Invoice generation for '
                                {data.invoiceGenerationInvokeType}' type is not
                                yet configured for manual selection. Only
                                Monthly generation is supported.
                              </p>
                            )}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
